import { useEffect } from 'react';

import { useAuthStore } from '@/stores/auth-store';

/** Must match the zustand persist `name` in stores/auth-store.ts. */
const AUTH_STORAGE_KEY = 'utazzo.auth';

/**
 * Keeps every open tab's auth state in sync across the browser.
 *
 * zustand's `persist` writes the session to localStorage but does NOT react to
 * changes made by OTHER tabs - each tab keeps its own in-memory copy. So when
 * one tab signs out (manual, or the inactivity auto-logout), the other tabs
 * kept their stale "authenticated" state and let the user keep navigating on a
 * still-valid cached token.
 *
 * The browser fires a `storage` event in every OTHER tab whenever localStorage
 * changes. Re-hydrating the store on our auth key means a logout in ANY tab
 * flips `isAuthenticated` to false everywhere at once - `ProtectedRoute` then
 * bounces each tab to /login (a backgrounded tab acts on its next paint /
 * refocus). A login syncs the same way.
 */
export const useCrossTabAuthSync = (): void => {
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      // key === null is a `localStorage.clear()`; also catch our auth key.
      if (e.key === AUTH_STORAGE_KEY || e.key === null) {
        void useAuthStore.persist.rehydrate();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
};
